import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LogOut, Factory, Route, ChevronRight, CalendarClock, Download, Menu, X, Search,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/layout/NotificationBell";
import StagingResetButton from "@/components/staging/StagingResetButton";
import useInstallPrompt from "@/hooks/useInstallPrompt";
import {
  NAV, ADMIN_NAV, PRODUCTION_FLOW_NAV, INCOMING_INSPECTION_NAV, DESIGN_NAV, REPORTS_NAV, CRM_NAV,
  useVisibleNav,
} from "@/components/layout/navConfig";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { canInstall, install } = useInstallPrompt();
  const nav = useNavigate();
  const location = useLocation();
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const { canSeeSalesReport, visibleAdminNav, visibleNav } = useVisibleNav(user);

  const [productionFlowOpen, setProductionFlowOpen] = useState(location.pathname.startsWith("/production-flow"));
  useEffect(() => {
    if (location.pathname.startsWith("/production-flow")) setProductionFlowOpen(true);
  }, [location.pathname]);

  const [incomingInspectionOpen, setIncomingInspectionOpen] = useState(location.pathname.startsWith("/quality/incoming-inspection"));
  useEffect(() => {
    if (location.pathname.startsWith("/quality/incoming-inspection")) setIncomingInspectionOpen(true);
  }, [location.pathname]);

  const [reportsOpen, setReportsOpen] = useState(location.pathname.startsWith("/reports"));
  useEffect(() => {
    if (location.pathname.startsWith("/reports")) setReportsOpen(true);
  }, [location.pathname]);

  const [designOpen, setDesignOpen] = useState(location.pathname.startsWith("/design"));
  useEffect(() => {
    if (location.pathname.startsWith("/design")) setDesignOpen(true);
  }, [location.pathname]);

  const [crmOpen, setCrmOpen] = useState(location.pathname.startsWith("/crm"));
  useEffect(() => {
    if (location.pathname.startsWith("/crm")) setCrmOpen(true);
  }, [location.pathname]);

  // Phones/small tablets: the sidebar becomes a slide-in drawer opened from
  // the topbar's menu button. It closes on navigation and on Escape.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [menuQuery, setMenuQuery] = useState("");
  useEffect(() => {
    setMobileNavOpen(false);
    setMenuQuery("");
  }, [location.pathname]);
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen]);

  // Drawer "Search menu" — a flat list of every link the sidebar would show
  // this user (top level + submenus), filtered by label. Navigation only.
  const SUBMENUS = {
    "/production-flow": PRODUCTION_FLOW_NAV,
    "/crm": CRM_NAV,
    "/design": DESIGN_NAV.filter((s) => !s.roles || s.roles.includes(user?.role)),
    "/reports": REPORTS_NAV,
    "/quality/incoming-inspection": INCOMING_INSPECTION_NAV,
  };
  const menuLinks = [
    ...visibleNav.flatMap((n) => [n, ...(SUBMENUS[n.to] || []).filter((c) => c.to !== n.to).map((c) => ({ ...c, icon: n.icon, parent: n.label }))]),
    ...visibleAdminNav,
    ...(canSeeSalesReport ? [{ to: "/sales-report", label: "Sales Report", icon: Route }] : []),
  ];
  const mq = menuQuery.trim().toLowerCase();
  const menuMatches = mq ? menuLinks.filter((n) => `${n.parent || ""} ${n.label}`.toLowerCase().includes(mq)) : [];
  const currentSection = [...NAV, ...ADMIN_NAV]
    .filter((n) => (n.to === "/" ? location.pathname === "/" : location.pathname === n.to || location.pathname.startsWith(n.to + "/")))
    .sort((a, b) => b.to.length - a.to.length)[0]?.label || "Dashboard";

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[var(--bg-canvas)]">
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 md:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
          data-testid="mobile-nav-backdrop"
        />
      )}
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col w-64 shrink-0 transition-transform duration-200 md:static md:z-auto md:translate-x-0 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ backgroundColor: "var(--sidebar-bg)" }}
        data-testid="sidebar"
      >
        <div className="px-5 py-5 flex items-center gap-3 border-b border-slate-800 max-md:px-4 max-md:py-3">
          <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center max-md:w-8 max-md:h-8">
            <Factory className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-white font-bold tracking-tight max-md:text-sm max-md:leading-tight" style={{ fontFamily: "Manrope" }}>Canares Automation</div>
            <div className="text-[11px] text-slate-400 uppercase tracking-[0.18em] max-md:text-[10px] max-md:tracking-[0.12em]">Manufacturing Suite</div>
          </div>
          <button
            type="button"
            className="ml-auto p-1 rounded text-slate-400 hover:text-white md:hidden"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close menu"
            data-testid="mobile-nav-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="md:hidden px-3 pt-3 pb-2 space-y-3 border-b border-slate-800" data-testid="mobile-nav-account">
          <div className="flex items-center gap-3 px-1">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="bg-blue-600 text-white text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
            </div>
            {user?.role && (
              <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wider text-blue-200 bg-blue-600/20 border border-blue-500/30 rounded px-1.5 py-0.5">
                {user.role}
              </span>
            )}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              value={menuQuery}
              onChange={(e) => setMenuQuery(e.target.value)}
              placeholder="Search menu…"
              aria-label="Search menu"
              className="w-full h-9 rounded-md bg-slate-800/80 border border-slate-700 pl-8 pr-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
              data-testid="mobile-nav-search"
            />
          </div>
        </div>
        {mq && (
          <div className="md:hidden flex-1 p-3 space-y-0.5 overflow-y-auto scroll-thin" data-testid="mobile-nav-results">
            {menuMatches.length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-500">No menu items match “{menuQuery}”.</div>}
            {menuMatches.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end || n.to === "/"} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                {n.icon ? <n.icon className="w-4 h-4 shrink-0" strokeWidth={2} /> : <span className="w-4" />}
                <span className="min-w-0">
                  {n.parent && <span className="block text-[10px] uppercase tracking-wider text-slate-500">{n.parent}</span>}
                  <span className="block truncate">{n.label}</span>
                </span>
              </NavLink>
            ))}
          </div>
        )}
        <nav className={`flex-1 p-3 space-y-1 overflow-y-auto scroll-thin ${mq ? "max-md:hidden" : ""}`}>
          {visibleNav.map((n) => {
            if (n.to === "/production-flow") {
              return (
                <div key={n.to}>
                  <button
                    type="button"
                    onClick={() => setProductionFlowOpen((v) => !v)}
                    className={`nav-item w-full justify-between ${location.pathname.startsWith("/production-flow") ? "active" : ""}`}
                    aria-expanded={productionFlowOpen}
                    data-testid="nav-production-flow"
                  >
                    <span className="flex items-center gap-3">
                      <n.icon className="w-4 h-4" strokeWidth={2} />
                      <span>{n.label}</span>
                    </span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${productionFlowOpen ? "rotate-90" : ""}`} />
                  </button>
                  {productionFlowOpen && (
                    <div className="mt-1 space-y-1" data-testid="nav-production-flow-submenu">
                      {PRODUCTION_FLOW_NAV.map((s) => (
                        <NavLink
                          key={s.to}
                          to={s.to}
                          end={s.end}
                          data-testid={s.tid}
                          className={({ isActive }) => `nav-item pl-9 ${isActive ? "active" : ""}`}
                        >
                          <span>{s.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            if (n.to === "/crm") {
              return (
                <div key={n.to}>
                  <button
                    type="button"
                    onClick={() => setCrmOpen((v) => !v)}
                    className={`nav-item w-full justify-between ${location.pathname.startsWith("/crm") ? "active" : ""}`}
                    aria-expanded={crmOpen}
                    data-testid="nav-crm"
                  >
                    <span className="flex items-center gap-3">
                      <n.icon className="w-4 h-4" strokeWidth={2} />
                      <span>{n.label}</span>
                    </span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${crmOpen ? "rotate-90" : ""}`} />
                  </button>
                  {crmOpen && (
                    <div className="mt-1 space-y-1" data-testid="nav-crm-submenu">
                      {CRM_NAV.map((s) => (
                        <NavLink
                          key={s.to}
                          to={s.to}
                          end={s.to === "/crm"}
                          data-testid={s.tid}
                          className={({ isActive }) => `nav-item pl-9 ${isActive ? "active" : ""}`}
                        >
                          <span>{s.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            if (n.to === "/design") {
              return (
                <div key={n.to}>
                  <button
                    type="button"
                    onClick={() => setDesignOpen((v) => !v)}
                    className={`nav-item w-full justify-between ${location.pathname.startsWith("/design") ? "active" : ""}`}
                    aria-expanded={designOpen}
                    data-testid="nav-design"
                  >
                    <span className="flex items-center gap-3">
                      <n.icon className="w-4 h-4" strokeWidth={2} />
                      <span>{n.label}</span>
                    </span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${designOpen ? "rotate-90" : ""}`} />
                  </button>
                  {designOpen && (
                    <div className="mt-1 space-y-1" data-testid="nav-design-submenu">
                      {DESIGN_NAV.filter((s) => !s.roles || s.roles.includes(user?.role)).map((s) => (
                        <NavLink
                          key={s.to}
                          to={s.to}
                          end={s.to === "/design"}
                          data-testid={s.tid}
                          className={({ isActive }) => `nav-item pl-9 ${isActive ? "active" : ""}`}
                        >
                          <span>{s.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            if (n.to === "/reports") {
              return (
                <div key={n.to}>
                  <button
                    type="button"
                    onClick={() => setReportsOpen((v) => !v)}
                    className={`nav-item w-full justify-between ${location.pathname.startsWith("/reports") ? "active" : ""}`}
                    aria-expanded={reportsOpen}
                    data-testid="nav-reports"
                  >
                    <span className="flex items-center gap-3">
                      <n.icon className="w-4 h-4" strokeWidth={2} />
                      <span>{n.label}</span>
                    </span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${reportsOpen ? "rotate-90" : ""}`} />
                  </button>
                  {reportsOpen && (
                    <div className="mt-1 space-y-1" data-testid="nav-reports-submenu">
                      {REPORTS_NAV.map((s) => (
                        <NavLink
                          key={s.to}
                          to={s.to}
                          data-testid={s.tid}
                          className={({ isActive }) => `nav-item pl-9 ${isActive ? "active" : ""}`}
                        >
                          <span>{s.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            if (n.to === "/quality/incoming-inspection") {
              return (
                <div key={n.to}>
                  <button
                    type="button"
                    onClick={() => setIncomingInspectionOpen((v) => !v)}
                    className={`nav-item w-full justify-between ${location.pathname.startsWith("/quality/incoming-inspection") ? "active" : ""}`}
                    aria-expanded={incomingInspectionOpen}
                    data-testid="nav-incoming-inspection"
                  >
                    <span className="flex items-center gap-3">
                      <n.icon className="w-4 h-4" strokeWidth={2} />
                      <span>{n.label}</span>
                    </span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${incomingInspectionOpen ? "rotate-90" : ""}`} />
                  </button>
                  {incomingInspectionOpen && (
                    <div className="mt-1 space-y-1" data-testid="nav-incoming-inspection-submenu">
                      {INCOMING_INSPECTION_NAV.map((s) => (
                        <NavLink
                          key={s.to}
                          to={s.to}
                          end={s.to === "/quality/incoming-inspection"}
                          data-testid={s.tid}
                          className={({ isActive }) => `nav-item pl-9 ${isActive ? "active" : ""}`}
                        >
                          <span>{s.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.tid}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <n.icon className="w-4 h-4" strokeWidth={2} />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
          {visibleAdminNav.length > 0 && (
            <>
              <div className="mt-4 mb-1 px-3 text-[10px] uppercase tracking-[0.18em] text-slate-500">Administration</div>
              {visibleAdminNav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.tid}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                >
                  <n.icon className="w-4 h-4" strokeWidth={2} />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </>
          )}
          {/* Daily Department Reports — new, additive only. Visible to every
              user regardless of role/module_permissions, same as Sales
              Report below; which department(s) they actually see is decided
              inside the page by their role. */}
          <div className="mt-4 pt-3 border-t border-slate-800">
            <NavLink
              to="/daily-reports"
              data-testid="nav-daily-reports"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <CalendarClock className="w-4 h-4" strokeWidth={2} />
              <span>Daily Reports</span>
            </NavLink>
          </div>
          {/* Sales Report — the live Sales Reporter app (pages/SalesReporterApp).
              Hidden only for users whose explicit module_permissions leave out
              "sales_report". */}
          {canSeeSalesReport && (
          <div className="mt-4 pt-3 border-t border-slate-800">
            <NavLink
              to="/sales-report"
              data-testid="nav-sales-report"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <Route className="w-4 h-4" strokeWidth={2} />
              <span>Sales Report</span>
            </NavLink>
          </div>
          )}
        </nav>
        <div className="p-4 border-t border-slate-800 text-[11px] text-slate-500 uppercase tracking-[0.18em] max-md:hidden">
          v1.0 · Canares Automation
        </div>
        <div className="md:hidden p-3 border-t border-slate-800">
          <button
            type="button"
            className="nav-item w-full"
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="mobile-logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={2} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header
          className="h-16 px-3 sm:px-6 flex items-center justify-between gap-2 border-b border-slate-200 bg-white/80 backdrop-blur-md max-md:h-14 max-md:bg-[#0B1120] max-md:border-slate-800 max-md:backdrop-blur-none"
          data-testid="topbar"
        >
          <button
            type="button"
            className="p-2 -ml-1 rounded-md text-white hover:bg-white/10 md:hidden"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-nav-open"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/" className="md:hidden min-w-0 flex-1 flex items-center gap-2" data-testid="mobile-brand">
            <span className="w-7 h-7 shrink-0 rounded bg-blue-600 flex items-center justify-center">
              <Factory className="w-4 h-4 text-white" />
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block text-sm font-bold text-white truncate" style={{ fontFamily: "Manrope" }}>Canares ERP</span>
              <span className="block text-[11px] text-slate-400 truncate">{currentSection}</span>
            </span>
          </Link>
          <div className="min-w-0 flex-1 md:flex-none max-md:hidden">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Plant</div>
            <div className="text-sm font-semibold text-slate-800 truncate" style={{ fontFamily: "Manrope" }}>
              Canares Automation — Line A
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0 max-md:gap-1">
            <StagingResetButton />
            {canInstall && (
              <Button
                variant="outline"
                size="sm"
                className="border-blue-200 text-blue-700 hover:bg-blue-50 max-md:h-8 max-md:px-2 max-md:border-slate-700 max-md:bg-transparent max-md:text-slate-100 max-md:hover:bg-white/10"
                onClick={install}
                data-testid="install-app-btn"
              >
                <Download className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Install app</span>
              </Button>
            )}
            <span className="contents max-md:[&_button]:text-slate-100 max-md:[&_button:hover]:bg-white/10 max-md:[&_button:hover]:text-white">
              <NotificationBell />
            </span>
            <Badge variant="outline" className="hidden md:inline-flex border-blue-200 text-blue-700 bg-blue-50 uppercase tracking-wider text-[10px]" data-testid="role-badge">
              {user?.role || ""}
            </Badge>
            <div className="flex items-center gap-2">
              <Avatar className="w-8 h-8 max-md:w-7 max-md:h-7">
                <AvatarFallback className="bg-blue-600 text-white text-xs">{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden md:block">
                <div className="text-sm font-medium text-slate-800">{user?.name}</div>
                <div className="text-xs text-slate-500">{user?.email}</div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-900 max-md:hidden"
              onClick={async () => { await logout(); nav("/login"); }}
              aria-label="Logout"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto scroll-thin p-3 sm:p-5 md:p-8 page-enter">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
